import { IChange } from 'json-diff-ts';
import { LngLatLike, Map } from 'maplibre-gl';
import { RuleGroupType, RuleType } from 'react-querybuilder';
import { Survey } from './metadata.types';

export interface MapObject {
  id: string;
  ref: Map | null;
  styleSpec: MapSpec;
  updateMapSpec: (newMapSpec: MapSpec) => void;
}

export interface VariableSelection {
  variableGuid: string;
  tableGuid: string;
  datasetAbbrev: string;
  surveyName: string;
  universeVariableUuid: string | undefined;
  isUserSurvey?: boolean;
}

export interface SurveyInfo {
  isUserSurvey: boolean;
  enabled: boolean;
  metadata: Survey;
  availableDatasets: string[] | null;
  availableSummaryLevels: string[] | null;
  defaultGbGeorecordSearchIndexId?: string;
}

export interface MapAnalysis {
  // TODO: MIGRATE
  type: 'geo' | 'manual'; // drawing, radius, drive time...
  selection: { id: string; label: string }[];
  fingerPrint: number;
  layer: {
    datasetId: string; // '365481',
    geoLayerId: string; // '316307',
    nameIdentifier: string; //  'Geo_QName',
    rowIdentifier: string; //__ID__
    summaryLevel: string; // 'SL050',
  };
}

export interface MapSpec {
  baseMapId: string;
  workingLayers: AnyWorkingLayer[];
  center: LngLatLike;
  zoom: number;
  analysis?: MapAnalysis; // TODO: MIGRATE
}

export interface GeoFeatureSelection {
  label: string;
  fips: string;
  summaryLevelId: string;
}

export interface MapContextObject {
  id: string;
  styleSpec: MapSpec;
  order: number;
}

export interface MapContext {
  id: string;
  type: 'single' | 'side' | 'side-locked' | 'swipe';
  mapObjects: MapContextObject[];
}

export enum VisualizationType {
  SHADED = 'shadedArea',
  BUBBLES = 'bubbles',
  DOT_DENSITY = 'dotDensity',
}

export interface CutPointSet {
  insufficientData?: {
    label: string;
    color: string;
  };
  cutPoints: CutPoint[];
}

export interface CutPoint {
  displayInLegend: boolean;
  from: number | undefined | string;
  to: number | undefined | string;
  title: string;
  color?: string;
}

export interface SimpleRenderingStyle {
  baseColor: string;
  opacities: number[];
}

export interface HeatRenderingStyle {
  baseColor: string;
  opacities: number[];
}

export interface ShadedRenderingStyle {
  opacities: number[];
  cutPointSets: CutPointSet[];
  colorPalette: {
    id: string;
    type: string;
  };
}

type WorkingLayerSource = 'social-explorer' | 'user-upload' | 'any-other';

export enum DataFilterType {
  COUNT = 'count',
  STRING = 'string',
  ENUM = 'enum',
  PERCENTAGE = 'pct',
}

export interface WorkingLayerFilterBaseRuleTypeValue {
  value: string | number;
  universeVariableId?: string;
  type?: DataFilterType; // default to DataFilterType.COUNT
}

export interface SocialExplorerWorkingLayerFilterRuleTypeValue extends WorkingLayerFilterBaseRuleTypeValue {
  tableId: string;
  datasetAbbreviation: string;
  surveyName: string;
}

export type WorkingLayerFilterRuleTypeBase = RuleType<string, string, WorkingLayerFilterBaseRuleTypeValue>;
export type WorkingLayerFilterRuleGroupBase = RuleGroupType<WorkingLayerFilterRuleTypeBase>;
export type SocialExplorerRuleType = RuleType<string, string, SocialExplorerWorkingLayerFilterRuleTypeValue>;
export type SocialExplorerRuleGroupType = RuleGroupType<SocialExplorerRuleType>;

export interface WorkingLayerFeatureSelection {
  name: string;
  id: string | number;
  metadata?: object;
}

export interface WorkingLayerVariableSelection {
  variableId: string;
  tableId: string;
  universeVariableId?: string;
}

export interface WorkingLayerGeoMaskingSelection {
  doMask: boolean;
  features: { name: string; id: string | number }[];
}

export type AnyWorkingLayer = SocialExplorerWorkingLayer | UserUploadedWorkingLayer;
export type SocialExplorerWorkingLayer = WorkingLayer<SocialExplorerWorkingLayerMetadata, SocialExplorerRuleGroupType>;
export type UserUploadedWorkingLayer = WorkingLayer<UserUploadedWorkingLayerMetadata, WorkingLayerFilterRuleGroupBase>;

export interface WorkingLayer<
  M extends WorkingLayerMetadataBase,
  F extends SocialExplorerRuleGroupType | WorkingLayerFilterRuleGroupBase
> {
  id: string;
  name: string;
  visible: boolean;
  type: LayerType;
  source: WorkingLayerSource;
  metadata: M;
  rendering: {
    type: 'simple' | 'shaded' | 'bubble' | 'heatmap';
    style: SimpleRenderingStyle | ShadedRenderingStyle | HeatRenderingStyle;
  };
  variableSelection: WorkingLayerVariableSelection[];
  featureSelection: WorkingLayerFeatureSelection[];
  maskingSelection: WorkingLayerGeoMaskingSelection;
  tooltip: WorkingLayerTooltip;
  description?: string;
  filter?: F;
}

export interface WorkingLayerMetadataBase {
  tiles: string;
  geoLayerId: string;
  dataLayerId: string;
  identifiers: {
    row: {
      variableId: string;
      datasetId: string;
    };
    name?: {
      variableId: string;
      datasetId: string;
    };
  };
}

export interface UserUploadedWorkingLayerMetadata extends WorkingLayerMetadataBase {
  datasetId: string;
}

export interface SocialExplorerWorkingLayerMetadata extends WorkingLayerMetadataBase {
  tiles: string;
  surveyName: string;
  datasetAbbreviation: string;
  isUserSurvey: boolean;
  geoLayerId: string;
  dataLayerId: string;
  identifiers: {
    row: {
      variableId: string;
      datasetId: string;
    };
    name?: {
      variableId: string;
      datasetId: string;
    };
  };
}

export interface TooltipLineItem {
  id: string;
  variableId: string;
  format?: string;
  label: string;
  description?: string;
  comparisonBase?: {
    type: 'pct';
    base: TooltipLineItem;
  };
}

export interface WorkingLayerTooltip {
  action: 'click';
  title: TooltipLineItem | undefined;
  content: TooltipLineItem[];
  enabled: boolean;
}

export enum ProjectType {
  DINGO_MAP = 'DINGO_MAP',
}

export enum StoryboardTemplate {
  SIDE_STRIP = 'SIDE_STRIP',
}

export enum FrameType {
  SINGLE_MAP = 'SINGLE_MAP',
}

export enum FrameLockMode {
  LOCKED_POSITION = 'LOCKED_POSITION',
}

export interface Variable {
  variableGuid: string;
  tableGuid: string;
  datasetAbbrev: string;
  surveyName: string;
  percentBaseVariableGuidUserOverride?: string;
}

export interface DataTheme {
  title: string;
  id: string;
  colorPaletteId: string;
  variableSelection: Variable[];
  rendering: Rendering;
}

export interface InitialView {
  centerLat: number;
  centerLon: number;
  zoom: number;
  boundingBoxNeLat: number;
  boundingBoxNeLng: number;
  boundingBoxSwLat: number;
  boundingBoxSwLng: number;
}

export interface Rendering {
  valueRenderer?: ValueRenderer;
}

export interface ValueRenderer {
  interactive: boolean;
  displayInLegend: boolean;
  defaultSymbolIndex: number;
  insufDataSymbol: string;
  nullDataSymbol: string;
  rules: RenderingRule[];
}

export interface RenderingRule {
  title: string;
  displayInLegend: boolean;
  zoomMin: number;
  zoomMax: number;
  filter: RenderingRuleFilter;
  polygonSymbol: PolygonSymbol;
}

export interface RenderingRuleFilter {
  label: string | null;
  fieldName: string;
  from: number;
  to: number;
}

export interface PolygonSymbol {
  id: string;
  brush: Brush;
}

export interface Brush {
  fill: string;
  fillOpacity: number;
  zoomMin: number;
  zoomMax: number;
}

export interface MapInstance {
  baseMapUid: string;
  currentMapContextId: string;
  initialView: InitialView;
  dataTheme: DataTheme;
  dataGeoFilters: DataGeoFilter[];
}

export interface DataGeoFilter {
  summaryLevelId: string;
  dataLayerId: string;
  nestedSummaryLevelsIds: string;
  features: {
    columnValue: string;
    geoQName: string;
  }[];
}

export interface Frame {
  type: FrameType;
  included: boolean;
  lockMode: FrameLockMode;
  mapInstances: MapInstance[];
}

export interface Storyboard {
  template: StoryboardTemplate;
  active: boolean;
  showThumbsInPlayMode: boolean;
  frames: Frame[];
}

export interface Project {
  version: string;
  storyboard: Storyboard;
}

export interface ProjectInfo {
  projectId: string;
  title: string;
  description: string;
  createdDate: string;
  modifiedDate: string;
  projectType: string;
  newProject: boolean;
  viewCode: string;
  etag: string;
  canSave: boolean;
  ownedBySe: boolean;
  projectThumbUrl: string;
  isShareSnapshot: boolean;
  assetsGuid: string;
  isPublic: boolean;
  loginRequired: boolean;
}

export interface ProjectFile {
  projectInfo: ProjectInfo;
  project: Project;
}

export interface SurveyToLoad {
  surveyCode: string;
  defaultGbGeorecordSearchIndexId?: string;
  availableSummaryLevels?: string[];
  availableDatasets?: string[];
  isUserSurvey?: boolean;
}

export interface PatchMapContext {
  type: string;
  diffs: Record<string, IChange[]>;
}

export enum ValueFormat {
  FORMAT_NONE = 'None',
  FORMAT_PERCENT = 'Percent',
  FORMAT_PERCENT_NO_DECIMAL = 'PercentNoDecimal',
  FORMAT_PERCENT_1_DECIMAL = 'Percent1Decimal',
  FORMAT_PERCENT_2_DECIMAL = 'Percent2Decimal',
  FORMAT_PERCENT_3_DECIMAL = 'Percent3Decimal',
  FORMAT_PERCENT_4_DECIMAL = 'Percent4Decimal',
  FORMAT_REAL_PERCENT = 'RealPercent',
  FORMAT_REAL_PERCENT_NO_DECIMAL = 'RealPercentNoDecimal',
  FORMAT_REAL_PERCENT_1_DECIMAL = 'RealPercent1Decimal',
  FORMAT_REAL_PERCENT_2_DECIMAL = 'RealPercent2Decimal',
  FORMAT_REAL_PERCENT_3_DECIMAL = 'RealPercent3Decimal',
  FORMAT_REAL_PERCENT_4_DECIMAL = 'RealPercent4Decimal',

  FORMAT_CURRENCY = 'Currency',
  FORMAT_CURRENCY_0_DECIMAL = 'Currency0Decimal',
  FORMAT_CURRENCY_1_DECIMAL = 'Currency1Decimal',
  FORMAT_CURRENCY_2_DECIMAL = 'Currency2Decimal',

  FORMAT_NUMBER = 'Number',
  FORMAT_NUMBER_NO_DECIMAL = 'NumberNoDecimal',
  FORMAT_NUMBER_1_DECIMAL = 'Number1Decimal',
  FORMAT_NUMBER_2_DECIMAL = 'Number2Decimal',
  FORMAT_NUMBER_3_DECIMAL = 'Number3Decimal',
  FORMAT_NUMBER_4_DECIMAL = 'Number4Decimal',
  FORMAT_NUMBER_5_DECIMAL = 'Number5Decimal',

  FORMAT_NUMBER_NO_FORMAT = 'NumberNoFormat',
  FORMAT_NUMBER_NO_FORMAT_NO_DECIMAL = 'NumberNoFormatNoDecimal',
  FORMAT_NUMBER_NO_FORMAT_1_DECIMAL = 'NumberNoFormat1Decimal',
  FORMAT_NUMBER_NO_FORMAT_2_DECIMAL = 'NumberNoFormat2Decimal',
  FORMAT_NUMBER_NO_FORMAT_3_DECIMAL = 'NumberNoFormat3Decimal',
  FORMAT_NUMBER_NO_FORMAT_4_DECIMAL = 'NumberNoFormat4Decimal',
  FORMAT_NUMBER_NO_FORMAT_5_DECIMAL = 'NumberNoFormat5Decimal',
}

export enum LayerType {
  POINT = 'POINT',
  POLYGON = 'POLYGON',
  LINE = 'LINE',
  TABLE = 'TABLE',
}

export enum MenuActions {
  WATCH,
  SHARE,
  EXPORT,
  CREATE_STORY,
  DELETE,
}

export enum MapTileSource {
  MAPTILER_SOURCE = 'source-maptiler',
}
